import React, { useState } from 'react';
import './JournalViewer.css';

const JournalViewer = ({ journalData, patient, cycleNumber }) => {
    const [selectedSection, setSelectedSection] = useState('selfAssessment');
    const [searchTerm, setSearchTerm] = useState('');

    const journalSections = [
        { key: 'selfAssessment', label: 'Self Assessment', icon: '📋' },
        { key: 'reflection', label: 'Reflection', icon: '💭' },
        { key: 'transformation', label: 'Transformation', icon: '🦋' },
        { key: 'checkIn01', label: 'Evaluation: Check-in #01', icon: '📝' },
        { key: 'checkIn02', label: 'Evaluation: Check-in #02', icon: '📅' },
        { key: 'checkIn03', label: 'Evaluation: Check-in #03', icon: '📅' },
        { key: 'checkIn04', label: 'Evaluation: Check-in #04', icon: '📅' },
        { key: 'checkIn05', label: 'Evaluation: Check-in #05', icon: '📅' },
        { key: 'checkIn06', label: 'Evaluation: Check-in #06', icon: '📅' }
    ];

    const formatDate = (dateString) => {
        if (!dateString) return 'Not completed';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPrimitiveValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return <span className="empty-value">Not provided</span>;
        }

        if (typeof value === 'boolean') {
            return <span className={`boolean-value ${value ? 'true' : 'false'}`}>
                {value ? 'Yes' : 'No'}
            </span>;
        }

        if (typeof value === 'string') {
            const normalized = value.toLowerCase();
            if (normalized === 'tick') return <span className="field-value">✓ Tick</span>;
            if (normalized === 'cross') return <span className="field-value">✗ Cross</span>;
        }

        return <span className="field-value">{String(value)}</span>;
    };

    const renderStructuredValue = (value, keyPath = '') => {
        if (value === null || value === undefined || value === '') {
            return <span className="empty-value">Not provided</span>;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return <span className="empty-value">None selected</span>;
            }

            return (
                <div className="array-value">
                    {value.map((item, index) => (
                        <span key={`${keyPath}-${index}`} className="array-item">
                            {typeof item === 'object' && item !== null
                                ? renderStructuredValue(item, `${keyPath}-${index}`)
                                : formatPrimitiveValue(item)}
                        </span>
                    ))}
                </div>
            );
        }

        if (typeof value === 'object') {
            const objectEntries = Object.entries(value);
            if (objectEntries.length === 0) {
                return <span className="empty-value">None selected</span>;
            }

            return (
                <div className="object-value">
                    {objectEntries.map(([objKey, objValue], index) => (
                        <div key={`${keyPath}-${objKey}-${index}`} className="object-item">
                            <strong>{formatFieldName(objKey)}:</strong>{' '}
                            {typeof objValue === 'object' && objValue !== null
                                ? renderStructuredValue(objValue, `${keyPath}-${objKey}`)
                                : formatPrimitiveValue(objValue)}
                        </div>
                    ))}
                </div>
            );
        }

        return formatPrimitiveValue(value);
    };

    const renderFieldValue = (value, key) => {
        if (value === null || value === undefined || value === '') {
            return <span className="empty-value">Not provided</span>;
        }

        if (typeof value === 'boolean') {
            return <span className={`boolean-value ${value ? 'true' : 'false'}`}>
                {value ? 'Yes' : 'No'}
            </span>;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return <span className="empty-value">None selected</span>;
            }
            return (
                <div className="array-value">
                    {value.map((item, index) => (
                        <span key={index} className="array-item">{item}</span>
                    ))}
                </div>
            );
        }

        // Handle objects (like symptoms, conditions, etc.)
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            return renderStructuredValue(value, key);
        }

        if (typeof value === 'string' && value.length > 100) {
            return (
                <div className="long-text">
                    <p>{value}</p>
                </div>
            );
        }

        return <span className="field-value">{String(value)}</span>;
    };

    const formatFieldName = (key) => {
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .replace(/([a-z])(\d)/g, '$1 $2')
            .replace(/(\d)([A-Z])/g, '$1 $2');
    };

    const filterFields = (data, searchTerm) => {
        if (!searchTerm) return data;

        const filtered = {};
        Object.entries(data).forEach(([key, value]) => {
            const formattedKey = formatFieldName(key).toLowerCase();
            const stringValue = String(value).toLowerCase();

            if (formattedKey.includes(searchTerm.toLowerCase()) ||
                stringValue.includes(searchTerm.toLowerCase())) {
                filtered[key] = value;
            }
        });

        return filtered;
    };

    const selectedData = journalData[selectedSection] || {};
    const filteredData = filterFields(selectedData, searchTerm);

    // Check if section has meaningful data (not just metadata)
    const meaningfulKeys = Object.keys(selectedData).filter(key =>
        key !== 'lastModified' &&
        key !== '_originalSection' &&
        key !== 'id' &&
        selectedData[key] !== null &&
        selectedData[key] !== undefined &&
        selectedData[key] !== ''
    );
    const hasData = meaningfulKeys.length > 0;

    return (
        <div className="journal-viewer">
            <div className="journal-viewer-header">
                <h2>Journal Entries {cycleNumber ? `— Cycle ${cycleNumber}` : ''}</h2>
                <input
                    type="text"
                    placeholder="Search in journal entries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="journal-search"
                />
            </div>

            <div className="journal-viewer-content">
                <div className="journal-sidebar">
                    <h3>Sections</h3>
                    <div className="journal-sections">
                        {journalSections.map(section => {
                            const sectionData = journalData[section.key];
                            // Check if section has meaningful data (not just lastModified)
                            const meaningfulKeys = sectionData ?
                                Object.keys(sectionData).filter(key =>
                                    key !== 'lastModified' &&
                                    key !== '_originalSection' &&
                                    key !== 'id' &&
                                    sectionData[key] !== null &&
                                    sectionData[key] !== undefined &&
                                    sectionData[key] !== ''
                                ) : [];
                            const isCompleted = meaningfulKeys.length > 0;

                            return (
                                <button
                                    key={section.key}
                                    className={`section-btn ${selectedSection === section.key ? 'active' : ''} ${isCompleted ? 'completed' : 'empty'}`}
                                    onClick={() => setSelectedSection(section.key)}
                                >
                                    <span className="section-icon">{section.icon}</span>
                                    <span className="section-label">{section.label}</span>
                                    <span className={`section-status ${isCompleted ? 'completed' : 'empty'}`}>
                                        {isCompleted ? '✓' : '○'}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="journal-content">
                    <div className="section-header">
                        <h3>{journalSections.find(s => s.key === selectedSection)?.label}</h3>
                        {selectedData.lastModified && (
                            <span className="last-modified">
                                Last updated: {formatDate(selectedData.lastModified)}
                            </span>
                        )}
                    </div>

                    {!hasData ? (
                        <div className="no-data">
                            <span className="no-data-icon">📝</span>
                            <h4>No data available</h4>
                            <p>This section hasn't been completed yet.</p>
                        </div>
                    ) : Object.keys(filteredData).length === 0 ? (
                        <div className="no-results">
                            <span className="no-results-icon">🔍</span>
                            <h4>No results found</h4>
                            <p>Try adjusting your search term.</p>
                        </div>
                    ) : (
                        <div className="journal-fields">
                            {Object.entries(filteredData)
                                .filter(([key]) => key !== 'lastModified' && key !== 'id' && key !== '_originalSection' && key !== 'cycle')
                                .map(([key, value]) => (
                                    <div key={key} className="field-row">
                                        <div className="field-label">
                                            {formatFieldName(key)}
                                        </div>
                                        <div className="field-content">
                                            {renderFieldValue(value, key)}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JournalViewer;